<div class="file-container">
  <!-- Loading / Error / Debug states -->
  <div *ngIf="loading" class="loading-indicator">
    Loading files... Please wait.
  </div>

  <div *ngIf="error" class="error-message">{{ error }}</div>

  <div *ngIf="!loading && !error && (!processingFiles || (processingFiles && processingFiles.length === 0))"
    class="debug-message">
    No file bundles available from server. If this persists, check that the API is configured correctly.
  </div>

  <!-- Selected bundle action bar -->
  <div *ngIf="checkedBundleId" class="selected-bundle-details">
    <span>id:{{ checkedBundleId }} (selected)</span>
    <button mat-button class="edit-button" (click)="editSelectedBundle()" matTooltip="Edit bundle details">
      <mat-icon>edit</mat-icon> Edit
    </button>
    <button mat-button class="delete-button" (click)="deleteSelectedBundle()" matTooltip="Delete this bundle">
      <mat-icon>delete</mat-icon> Delete
    </button>
  </div>

  <!-- ===================== MAIN TABLE ===================== -->
  <div *ngIf="processingFiles && processingFiles.length > 0" class="processing-files">
    <div class="mat-elevation-z8 file-grid-container">
      <table mat-table [dataSource]="processingFiles" class="mat-file-table">

        <!-- Checkbox Column -->
        <ng-container matColumnDef="checkbox">
          <th mat-header-cell *matHeaderCellDef></th>
          <td mat-cell *matCellDef="let bundle">
            <mat-checkbox
              (change)="onCheckboxChange(bundle)"
              [checked]="checkedBundleId === bundle.id"
              (click)="$event.stopPropagation()">
            </mat-checkbox>
          </td>
        </ng-container>

        <!-- ID Column -->
        <ng-container matColumnDef="id">
          <th mat-header-cell *matHeaderCellDef>id</th>
          <td mat-cell *matCellDef="let bundle">{{ bundle.id || 'N/A' }}</td>
        </ng-container>

        <!-- Subject Column -->
        <ng-container matColumnDef="subject">
          <th mat-header-cell *matHeaderCellDef class="resizable-column">
            <div class="resize-header">
              <span>Subject</span>
              <div class="resize-handle" (mousedown)="startResize($event, 'subject')" title="Drag to resize column width"></div>
            </div>
          </th>
          <td mat-cell *matCellDef="let bundle">{{ bundle.subject || 'N/A' }}</td>
        </ng-container>

        <!-- Section Column -->
        <ng-container matColumnDef="section">
          <th mat-header-cell *matHeaderCellDef class="resizable-column">
            <div class="resize-header">
              <span>Section</span>
              <div class="resize-handle" (mousedown)="startResize($event, 'section')" title="Drag to resize column width"></div>
            </div>
          </th>
          <td mat-cell *matCellDef="let bundle">{{ bundle.section || 'N/A' }}</td>
        </ng-container>

        <!-- Topic Column -->
        <ng-container matColumnDef="topic">
          <th mat-header-cell *matHeaderCellDef class="resizable-column">
            <div class="resize-header">
              <span>Topic</span>
              <div class="resize-handle" (mousedown)="startResize($event, 'topic')" title="Drag to resize column width"></div>
            </div>
          </th>
          <td mat-cell *matCellDef="let bundle">{{ bundle.topic || 'N/A' }}</td>
        </ng-container>

        <!-- Subtopic Column -->
        <ng-container matColumnDef="subtopic">
          <th mat-header-cell *matHeaderCellDef class="resizable-column">
            <div class="resize-header">
              <span>Subtopic</span>
              <div class="resize-handle" (mousedown)="startResize($event, 'subtopic')" title="Drag to resize column width"></div>
            </div>
          </th>
          <td mat-cell *matCellDef="let bundle">{{ bundle.subtopic || 'N/A' }}</td>
        </ng-container>

        <!-- ========== FILE TYPE COLUMNS (dynamic via *ngFor) ========== -->
        <ng-container *ngFor="let type of regularFileTypes" [matColumnDef]="type">
          <th mat-header-cell *matHeaderCellDef class="file-type-column">
            <div class="file-type-header" [ngClass]="type + '-header'">{{ type.toUpperCase() }}</div>
          </th>
          <td mat-cell *matCellDef="let bundle; let bundleIndex = index"
              class="file-type-cell"
              [ngClass]="{
                'no-file-cell': !fileExistsInBundle(type, bundle),
                'user-selected-file': fileExistsInBundle(type, bundle) && selectedFileType === type && selectedBundleIndex === bundleIndex
              }">
            <div class="file-card-compact">

              <!-- In-cell preview (when preview mode enabled & file exists) -->
              <div *ngIf="shouldShowInCellPreview() && fileExistsInBundle(type, bundle)" class="file-preview-container">
                <div *ngIf="isCellPreviewLoading(type, bundleIndex)" class="preview-loading">
                  <mat-spinner diameter="24"></mat-spinner>
                  <span>Loading preview...</span>
                </div>
                <div *ngIf="getCellPreviewError(type, bundleIndex)" class="preview-error">
                  <mat-icon>error</mat-icon>
                  <span>{{ getCellPreviewError(type, bundleIndex) }}</span>
                </div>

                <!-- Preview content — switched by render type -->
                <ng-container *ngIf="!isCellPreviewLoading(type, bundleIndex) && !getCellPreviewError(type, bundleIndex) && getCellPreviewContent(type, bundleIndex)">
                  <div *ngIf="getPreviewRenderType(type) === 'text'"
                       class="preview-content txt-preview"
                       (click)="viewFileFromBundle(type, bundleIndex); $event.stopPropagation()">
                    <div class="preview-text">{{ getCellPreviewContent(type, bundleIndex) }}</div>
                  </div>
                  <div *ngIf="getPreviewRenderType(type) === 'innerHTML'"
                       class="preview-content" [ngClass]="type + '-preview'"
                       (click)="viewFileFromBundle(type, bundleIndex); $event.stopPropagation()"
                       [innerHTML]="getCellPreviewContent(type, bundleIndex)">
                  </div>
                  <div *ngIf="getPreviewRenderType(type) === 'iframe'"
                       class="preview-content" [ngClass]="type + '-preview'"
                       (click)="viewFileFromBundle(type, bundleIndex); $event.stopPropagation()">
                    <iframe [src]="getCellPreviewContent(type, bundleIndex)" width="100%" height="100%"></iframe>
                  </div>
                  <div *ngIf="getPreviewRenderType(type) === 'image'"
                       class="preview-content image-preview"
                       (click)="viewFileFromBundle(type, bundleIndex); $event.stopPropagation()">
                    <img [src]="getCellPreviewContent(type, bundleIndex)" [alt]="getFileNameFromBundle(type, bundle)" />
                  </div>
                </ng-container>

                <!-- Load-on-demand placeholder -->
                <div *ngIf="shouldLoadOnDemand() && !isCellPreviewLoading(type, bundleIndex) && !getCellPreviewError(type, bundleIndex) && !getCellPreviewContent(type, bundleIndex)"
                     class="preview-placeholder"
                     (click)="viewFileFromBundle(type, bundleIndex); $event.stopPropagation()">
                  <mat-icon>{{ getPreviewIcon(type) }}</mat-icon>
                  <span>Click to load preview</span>
                </div>

                <div class="file-info-overlay">
                  <span class="file-name-text">{{ getFileNameFromBundle(type, bundle) }}</span>
                </div>
              </div>

              <!-- Regular file info (when not in preview mode) -->
              <div *ngIf="!shouldShowInCellPreview() && fileExistsInBundle(type, bundle)" class="file-info-compact"
                   (click)="viewFileFromBundle(type, bundleIndex); $event.stopPropagation()">
                <span class="file-name-text">{{ getFileNameFromBundle(type, bundle) }}</span>
                <div *ngIf="selectedHeight === 'large'" class="file-date-compact">
                  Last modified: {{ formatDate(getFilePropertyFromBundle(type, 'modified_at', bundle)) }}
                </div>
              </div>

              <!-- Thumbnail mode -->
              <div *ngIf="shouldShowThumbnails() && fileExistsInBundle(type, bundle)" class="file-thumbnail-container">
                <div *ngIf="thumbnailExistsInBundle(type, bundle)" class="thumbnail-display"
                     (click)="viewFileFromBundle(type, bundleIndex); $event.stopPropagation()">
                  <div *ngIf="isThumbnailLoading(type, bundleIndex)" class="thumbnail-loading">
                    <mat-spinner diameter="24"></mat-spinner>
                    <span>Loading...</span>
                  </div>
                  <div *ngIf="getThumbnailError(type, bundleIndex)" class="thumbnail-error">
                    <mat-icon>error</mat-icon>
                    <span>Error loading thumbnail</span>
                  </div>
                  <img *ngIf="!isThumbnailLoading(type, bundleIndex) && !getThumbnailError(type, bundleIndex) && getThumbnailUrlFromBundle(type, bundle)"
                       [src]="getThumbnailUrlFromBundle(type, bundle)"
                       [alt]="getThumbnailFilenameFromBundle(type, bundle)"
                       class="file-thumbnail-img"
                       (error)="onThumbnailImageError($event)"
                       (load)="onThumbnailImageLoad($event)">
                </div>
                <div *ngIf="!thumbnailExistsInBundle(type, bundle)" class="no-thumbnail-message"
                     (click)="viewFileFromBundle(type, bundleIndex); $event.stopPropagation()">
                  <mat-icon>image_not_supported</mat-icon>
                  <span>No thumbnail</span>
                </div>
              </div>

              <!-- No file message -->
              <div *ngIf="!fileExistsInBundle(type, bundle)" class="no-file-message">
                No {{ type.toUpperCase() }} file available
              </div>

              <!-- Action buttons — always visible, icon-only -->
              <div class="file-actions-compact">
                <button mat-icon-button class="action-btn" [ngClass]="getDownloadButtonClass()"
                  [disabled]="!fileExistsInBundle(type, bundle) || isFileLoading(type, 'download', bundleIndex)"
                  (click)="downloadProcessingFileFromBundle(type, bundleIndex); $event.stopPropagation()"
                  [matTooltip]="'Download ' + getFileTypeTooltipLabel(type)">
                  <mat-icon>download</mat-icon>
                </button>
                <ng-container *ngIf="userType === 'internal'">
                  <button mat-icon-button class="action-btn ul-btn"
                    [disabled]="fileExistsInBundle(type, bundle) || uploading[type + '-' + bundleIndex]"
                    (click)="triggerFileUploadForBundle(type, bundleIndex); $event.stopPropagation()"
                    [matTooltip]="'Upload new ' + getFileTypeTooltipLabel(type)">
                    <mat-icon>upload</mat-icon>
                  </button>
                  <button mat-icon-button class="action-btn rp-btn"
                    [disabled]="!fileExistsInBundle(type, bundle) || uploading[type + '-' + bundleIndex]"
                    (click)="replaceFileInSpecificBundle(type, bundleIndex); $event.stopPropagation()"
                    [matTooltip]="'Replace existing ' + getFileTypeTooltipLabel(type)">
                    <mat-icon>sync</mat-icon>
                  </button>
                  <button mat-icon-button class="action-btn del-btn"
                    [disabled]="!fileExistsInBundle(type, bundle) || uploading[type + '-' + bundleIndex]"
                    (click)="deleteProcessingFileFromBundle(type, bundleIndex); $event.stopPropagation()"
                    [matTooltip]="'Delete ' + getFileTypeTooltipLabel(type)">
                    <mat-icon>delete</mat-icon>
                  </button>
                </ng-container>
              </div>
            </div>
          </td>
        </ng-container>

        <!-- ========== YOUTUBE COLUMN (special handling) ========== -->
        <ng-container matColumnDef="youtube">
          <th mat-header-cell *matHeaderCellDef class="file-type-column">
            <div class="file-type-header youtube-header">YOUTUBE</div>
          </th>
          <td mat-cell *matCellDef="let bundle; let bundleIndex = index"
              class="file-type-cell youtube-cell"
              [ngClass]="{
                'no-file-cell': !hasYouTubeVideo(bundle),
                'user-selected-file': hasYouTubeVideo(bundle) && selectedFileType === 'youtube' && selectedBundleIndex === bundleIndex
              }">
            <div class="file-card-compact youtube-card">
              <div *ngIf="hasYouTubeVideo(bundle)" class="youtube-info-compact"
                   (click)="viewYouTubeVideo(bundle); $event.stopPropagation()">
                <div class="youtube-thumbnail">
                  <img [src]="bundle.youtube?.thumbnail_url"
                       [alt]="bundle.youtube?.title"
                       class="youtube-thumb-img"
                       (error)="onYouTubeThumbnailError($event)">
                  <div class="play-overlay">
                    <mat-icon class="play-icon">play_circle_filled</mat-icon>
                  </div>
                </div>
                <div class="youtube-title">{{ bundle.youtube?.title || 'YouTube Video' }}</div>
                <div *ngIf="selectedHeight === 'large'" class="file-date-compact">
                  Added: {{ formatDate(bundle.youtube?.created_at || '') }}
                </div>
              </div>
              <div *ngIf="!hasYouTubeVideo(bundle)" class="no-file-message">
                No YouTube video available
              </div>
              <div class="file-actions-compact youtube-actions">
                <button mat-icon-button class="action-btn watch-btn"
                  [disabled]="!hasYouTubeVideo(bundle)"
                  (click)="openYouTubeVideo(bundle.youtube?.watch_url); $event.stopPropagation()"
                  matTooltip="Watch on YouTube">
                  <mat-icon>play_arrow</mat-icon>
                </button>
                <ng-container *ngIf="userType === 'internal'">
                  <button mat-icon-button class="action-btn ul-btn"
                    [disabled]="hasYouTubeVideo(bundle)"
                    (click)="addYouTubeVideo(bundleIndex); $event.stopPropagation()"
                    matTooltip="Add YouTube video">
                    <mat-icon>add</mat-icon>
                  </button>
                  <button mat-icon-button class="action-btn rp-btn"
                    [disabled]="!hasYouTubeVideo(bundle)"
                    (click)="updateYouTubeVideo(bundleIndex); $event.stopPropagation()"
                    matTooltip="Update YouTube video">
                    <mat-icon>edit</mat-icon>
                  </button>
                  <button mat-icon-button class="action-btn del-btn"
                    [disabled]="!hasYouTubeVideo(bundle)"
                    (click)="removeYouTubeVideo(bundleIndex); $event.stopPropagation()"
                    matTooltip="Remove YouTube video">
                    <mat-icon>delete</mat-icon>
                  </button>
                </ng-container>
              </div>
            </div>
          </td>
        </ng-container>

        <!-- Table row definitions -->
        <tr mat-header-row *matHeaderRowDef="displayedColumns; sticky: true"></tr>
        <tr mat-row *matRowDef="let bundle; let bundleIndex = index; columns: displayedColumns;"
            [class.selected-bundle]="selectedBundleIndex === bundleIndex"
            (click)="selectBundle(bundleIndex)"></tr>
      </table>
    </div>
  </div>

  <!-- Empty state -->
  <div *ngIf="processingFiles && processingFiles.length === 0" class="empty-state">
    <p>No file bundles available.</p>
  </div>

  <!-- Hidden file input for upload/replace -->
  <input #fileInput type="file" style="display: none;" (change)="onFileSelected($event, 'txt')">

  <!-- Login prompt -->
  <div *ngIf="showLoginPrompt" class="login-prompt">
    <h3>Login Required</h3>
    <p>Please log in with your Google ID to download the content.</p>
    <app-login-gmail buttonIdSuffix="FileProcessing" (userLoggedIn)="onUserLoggedIn($event)"></app-login-gmail>
  </div>
</div>
